/**
 * Korpus Builder v2 — čistý generátor (F1).
 *
 * Vypočíta otvory (rekurzívne delenie medzistenami) a vygeneruje
 * dielce + kovanie z parametrov. Bez React závislostí — unit-testované.
 *
 * Súradnice otvorov: vnútorný priestor korpusu, (0,0) = ľavý DOLNÝ
 * vnútorný roh (medzi bokmi, nad dnom). Vnútro je pre všetky typy
 * konštrukcie (šírka − 2t) × (výška − 2t) — mení sa len to, ktoré
 * dosky bežia cez celú mieru (dĺžka boku / šírka plátu a dna).
 */

import { MEDZERA_CIEL } from "./types";
import type {
  ChrbatTyp,
  Delenie,
  DvierkaConfig,
  GenDielec,
  GenKovanie,
  GenVysledok,
  KorpusParams,
  Otvor,
  Rect,
  TypCela,
} from "./types";

let seq = 1;

/** Nový prázdny otvor (unikátne id v rámci session). */
export function novyOtvor(): Otvor {
  return { id: `o${seq++}`, police: 0 };
}

/** Počet nôh podľa šírky — KONSTRUKCIE.md §8. */
export function pocetNoh(sirkaMm: number): number {
  if (sirkaMm <= 800) return 4;
  if (sirkaMm <= 1200) return 6;
  return 8;
}

/** Počet podpier na policu podľa hĺbky — KONSTRUKCIE.md §7. */
export function podperyNaPolicu(hlbkaMm: number): number {
  return hlbkaMm <= 600 ? 4 : 6;
}

/** Merná hmotnosť DTD (kg/m² pri 18 mm) — FITTINGS.md §Počet závesov. */
const DTD_KG_M2_18 = 19.5;

/**
 * Reálne dielenské kódy závesov (overené TopSolid exportmi 07/2026):
 * naložené 56-3550, vložené 56-3690. Polonaložené zatiaľ katalógový
 * Blum 71B3650 — reálny kód z dielne zatiaľ nemáme.
 */
export const ZAVES_KATALOG: Record<TypCela, string> = {
  nalozene: "56-3550",
  vlozene: "56-3690",
  polonalozene: "71B3650",
};

/** Krížová montážna podložka CLIP — 1 ks na každý záves (dielňa). */
export const PODLOZKA_KATALOG = "56-0248";

/** Popis variantu závesu podľa typu čela — do poznámky kovania. */
const ZAVES_VARIANT: Record<TypCela, string> = {
  nalozene: "naložené → 56-3550",
  polonalozene: "polonaložené → 71B3650",
  vlozene: "vložené → 56-3690",
};

/**
 * Počet závesov na jedno krídlo podľa hmotnosti — FITTINGS.md:
 * do 6 kg: 2, 6–12 kg: 3, nad 12 kg: 4; šírka krídla nad 600 mm: +1.
 * Hmotnosť sa škáluje hrúbkou (t/18).
 */
export function pocetZavesov(
  sirkaKridlaMm: number,
  vyskaKridlaMm: number,
  tMm = 18,
): number {
  const plochaM2 = (sirkaKridlaMm * vyskaKridlaMm) / 1_000_000;
  const kg = plochaM2 * DTD_KG_M2_18 * (tMm / 18);
  let n = kg <= 6 ? 2 : kg <= 12 ? 3 : 4;
  if (sirkaKridlaMm > 600) n += 1;
  return n;
}

/** Rad nominálnych dĺžok guličkových výsuvov (mm) — catalog/riex.json. */
export const VYSUV_NL_RAD = [550, 500, 450, 400, 350, 300, 250] as const;

/**
 * Výška boku boxu zásuvky — naučené z reálnych TopSolid exportov
 * (07/2026, korpus_zasuvky_gulickovy_*): naložené čelá
 * (výška otvoru − 240)/N, vložené výška otvoru/N − 40.
 * Polonaložené bez vzorky → naložený vzorec.
 */
export function vyskaBokuZasuvky(
  vyskaOtvoru: number,
  pocet: number,
  typ: TypCela,
): number {
  const surova =
    typ === "vlozene"
      ? vyskaOtvoru / pocet - 40
      : (vyskaOtvoru - 240) / pocet;
  return Math.round(surova * 10) / 10;
}

/** Vôľa guličkového výsuvu na stranu — box je o 2×13 mm užší než otvor. */
export const VOLA_VYSUVU = 13;

/**
 * Šírka čela/chrbta boxu = otvor − (2×13 vôľa výsuvu + 2×t boky boxu
 * + 1 mm). Reálne TopSolid exporty mali len t = 18, odtiaľ −63; boky
 * boxu sú ale z dosky slotu (KOLO 168: hrúbka podľa materiálu), takže
 * pri 16 mm je odpočet 59 — konštanta 63 by nechala box o 4 mm užší
 * a výsuv by nedosadol. Chrbát boxu je o 14 mm nižší.
 */
export function boxSirkaOdpocet(t: number): number {
  return 2 * VOLA_VYSUVU + 2 * t + 1;
}
export const BOX_CHRBAT_NIZSI = 14;

/**
 * Dno boxu (sololit 4 mm) = (NL − 10) × (otvor − (2×13 + 2×t − 15)):
 * šírka medzi bokmi boxu + 2 × 7,5 mm v drážkach bokov. Pri t = 18 je
 * to −47 z reálnych exportov.
 */
export function dnoSirkaOdpocet(t: number): number {
  return 2 * VOLA_VYSUVU + 2 * t - 15;
}
export const DNO_NL_ODPOCET = 10;

/**
 * Najdlhší výsuv, ktorý sa zmestí do hĺbky korpusu — FITTINGS.md:
 * min. hĺbka korpusu = NL + 4 mm. Null = korpus je priplytký.
 */
export function vyberNlVysuvu(hlbkaKorpusuMm: number): number | null {
  return VYSUV_NL_RAD.find((nl) => nl + 4 <= hlbkaKorpusuMm) ?? null;
}

/** Legacy hodnota "nasadene" (staré +20 mm vzorce) → naložené;
 *  drží uložené šablóny/konfigy funkčné — KONSTRUKCIE.md §14. */
export function normalizujTypCela(v: string | null | undefined): TypCela {
  if (v === "vlozene" || v === "polonalozene") return v;
  return "nalozene";
}

/** Kontext otvoru pre výpočet čiel — vnútro korpusu (š−2t × v−2t). */
export interface CeloCtx {
  t: number;
  innerW: number;
  innerH: number;
}

/** Prekrytie jednej strany čela (KONSTRUKCIE.md §14): naložené t−2 na
 *  vonkajšej hrane / t/2−1 na medzistene; polonaložené t/2−1 všade;
 *  vložené −2 (do otvoru). */
function presahStrany(typ: TypCela, vonkajsia: boolean, t: number): number {
  if (typ === "vlozene") return -2;
  if (typ === "polonalozene") return t / 2 - 1;
  return vonkajsia ? t - 2 : t / 2 - 1;
}

/**
 * Celková čelná plocha (dvere alebo blok čiel zásuviek) pre otvor.
 * Vracia mm vo vnútorných súradniciach — pri naložených presahuje
 * otvor (x0 < otvor.x, w > otvor.w).
 */
export function rozmerCelnejPlochy(
  otvor: Rect,
  typ: TypCela,
  ctx: CeloCtx,
): { x0: number; y0: number; w: number; h: number } {
  const { t, innerW, innerH } = ctx;
  const eps = 0.5; // stred delenia môže dať neceločíselné hranice
  const lava = presahStrany(typ, otvor.x <= eps, t);
  const prava = presahStrany(typ, otvor.x + otvor.w >= innerW - eps, t);
  const dolna = presahStrany(typ, otvor.y <= eps, t);
  const horna = presahStrany(typ, otvor.y + otvor.h >= innerH - eps, t);
  return {
    x0: otvor.x - lava,
    y0: otvor.y - dolna,
    w: otvor.w + lava + prava,
    h: otvor.h + dolna + horna,
  };
}

/** Rozmer krídel dverí z otvoru — KONSTRUKCIE.md §14. Dve krídla
 *  delia čelnú plochu s 2 mm stredovou medzerou. */
export function rozmerDvierok(
  otvor: Rect,
  dvierka: DvierkaConfig,
  ctx: CeloCtx,
): { x0: number; y0: number; sirkaKridla: number; vyska: number } {
  const typ = normalizujTypCela(dvierka.typ);
  const plocha = rozmerCelnejPlochy(otvor, typ, ctx);
  const n = dvierka.kridla;
  const sirkaKridla = (plocha.w - (n - 1) * MEDZERA_CIEL) / n;
  return {
    x0: plocha.x0,
    y0: plocha.y0,
    sirkaKridla: Math.round(sirkaKridla),
    vyska: Math.round(plocha.h),
  };
}

/** Os medzisteny v mm od dolnej/ľavej hrany otvoru. */
export function osDelenia(otvor: Rect, d: Delenie): number {
  const rozmer = d.smer === "vodorovne" ? otvor.h : otvor.w;
  switch (d.ref) {
    case "stred":
      return rozmer / 2;
    case "od_dna":
    case "od_boku_l":
      return d.mm;
    case "od_stropu":
    case "od_boku_p":
      return rozmer - d.mm;
  }
}

/** Validácia osi: medzistena sa musí zmestiť do otvoru. */
export function osJeValidna(otvor: Rect, d: Delenie, t: number): boolean {
  const rozmer = d.smer === "vodorovne" ? otvor.h : otvor.w;
  const os = osDelenia(otvor, d);
  return os >= t / 2 && os <= rozmer - t / 2;
}

/**
 * Rekurzívne vypočíta obdĺžniky všetkých otvorov (aj vnútorných uzlov —
 * uzol s delením má rect celého pôvodného otvoru). Nevalidné delenia
 * hlási do `chyby` a ich deti preskočí.
 */
export function vypocitajOtvory(
  params: KorpusParams,
  root: Otvor,
): { rects: Map<string, Rect>; chyby: string[] } {
  const { sirka, vyska, t } = params;
  const rects = new Map<string, Rect>();
  const chyby: string[] = [];

  function walk(o: Otvor, rect: Rect): void {
    rects.set(o.id, rect);
    if (!o.delenie) return;
    const d = o.delenie;
    if (!osJeValidna(rect, d, t)) {
      chyby.push(
        `Medzistena v otvore mimo rozsahu (${d.smer}, ${d.ref}${d.ref !== "stred" ? ` ${d.mm} mm` : ""}).`,
      );
      return;
    }
    const os = osDelenia(rect, d);
    if (d.smer === "vodorovne") {
      // dolný + horný
      walk(d.deti[0], { x: rect.x, y: rect.y, w: rect.w, h: os - t / 2 });
      walk(d.deti[1], {
        x: rect.x,
        y: rect.y + os + t / 2,
        w: rect.w,
        h: rect.h - os - t / 2,
      });
    } else {
      // ľavý + pravý
      walk(d.deti[0], { x: rect.x, y: rect.y, w: os - t / 2, h: rect.h });
      walk(d.deti[1], {
        x: rect.x + os + t / 2,
        y: rect.y,
        w: rect.w - os - t / 2,
        h: rect.h,
      });
    }
  }

  walk(root, { x: 0, y: 0, w: sirka - 2 * t, h: vyska - 2 * t });
  return { rects, chyby };
}

/** Dĺžka boku podľa typu konštrukcie (potvrdené z dielne). */
export function dlzkaBoku(params: KorpusParams): number {
  const { vyska, t, typ } = params;
  if (typ === "vlozeny_plat") return vyska; // plát+dno medzi bokmi
  return vyska - 2 * t; // naložený plát: plát+dno cez boky
}

/** Šírky plátu a dna podľa typu konštrukcie. */
export function sirkyPlatuADna(params: KorpusParams): {
  plat: number;
  dno: number;
} {
  const { sirka, t, typ } = params;
  if (typ === "nalozeny_plat") return { plat: sirka, dno: sirka };
  return { plat: sirka - 2 * t, dno: sirka - 2 * t }; // vložený plát
}

/** Hĺbka medzisteny podľa chrbta: HDF drážka −12, vložený LTD −t,
 *  pribíjaný sololit −4, bez chrbta plná hĺbka. */
export function hlbkaMedzisteny(
  hlbka: number,
  chrbat: ChrbatTyp,
  t: number,
): number {
  switch (chrbat) {
    case "hdf":
      return hlbka - 12;
    case "ltd":
      return hlbka - t;
    case "sololit":
      return hlbka - 4;
    case "none":
      return hlbka;
  }
}

/** Odpočet hĺbky police od hĺbky korpusu — naučené z reálnych
 *  TopSolid exportov: sololit/bez chrbta −6, HDF v drážke −18;
 *  vložený LTD chrbát −(t + 2) — polica končí 2 mm pred chrbtom hrúbky
 *  t (exporty mali t = 18, teda −20; pri 27 mm doske by −20 zasahovalo
 *  7 mm do chrbta). */
export function odpocetPolice(chrbat: ChrbatTyp, t: number): number {
  if (chrbat === "hdf") return 18;
  if (chrbat === "ltd") return t + 2;
  return 6; // sololit aj none
}

/** Police za VLOŽENÝMI dvierkami: skrátené o 20 mm a posunuté dozadu
 *  (dvere sedia v otvore) — reálne TopSolid exporty 07/2026. */
export const POLICA_SETBACK_VLOZENE = 20;

export function policeZaVlozenymi(o: Otvor): boolean {
  return !!o.dvierka && normalizujTypCela(o.dvierka.typ) === "vlozene";
}

/**
 * Vygeneruje dielce + kovanie celého korpusu.
 *
 * Drážka na HDF chrbát sa účtuje ako operácia `drazka_7mm` (€/m ×
 * dĺžka X dielca) na bokoch, pláte a dne — súčet = obvod drážky.
 */
export function generujKorpus(params: KorpusParams, root: Otvor): GenVysledok {
  const { sirka, vyska, hlbka, t, chrbat, nohy, presahy } = params;
  const dielce: GenDielec[] = [];
  const kovanie: GenKovanie[] = [];

  const { rects, chyby } = vypocitajOtvory(params, root);

  const drazka = chrbat === "hdf" ? "drazka_7mm" : undefined;
  const bok = dlzkaBoku(params);
  const { plat, dno } = sirkyPlatuADna(params);

  // presahy predlžujú dosku oproti korpusu — vnútro/otvory nemenia
  const pr = presahy;
  dielce.push(
    { nazov: "bok_L", x: bok + pr.bok_l.a + pr.bok_l.b, y: hlbka + pr.bok_l.predny + pr.bok_l.zadny, z: t, ks: 1, operacia: drazka, cnc: true },
    { nazov: "bok_P", x: bok + pr.bok_p.a + pr.bok_p.b, y: hlbka + pr.bok_p.predny + pr.bok_p.zadny, z: t, ks: 1, operacia: drazka, cnc: true },
    { nazov: "plat", x: plat + pr.plat.a + pr.plat.b, y: hlbka + pr.plat.predny + pr.plat.zadny, z: t, ks: 1, operacia: drazka, cnc: true },
    { nazov: "dno", x: dno + pr.dno.a + pr.dno.b, y: hlbka + pr.dno.predny + pr.dno.zadny, z: t, ks: 1, operacia: drazka, cnc: true },
  );

  if (chrbat === "hdf") {
    // drážka 8 mm hlboká → chrbát presahuje vnútro o 8 mm na stranu
    dielce.push({
      nazov: "chrbat",
      x: sirka - 2 * t + 16,
      y: vyska - 2 * t + 16,
      z: 4,
      ks: 1,
      cnc: true,
    });
  } else if (chrbat === "ltd") {
    // VLOŽENÝ plnohodnotný chrbát (V−2t × Š−2t × t) — overené
    // reálnym TopSolid exportom (korpus_nalozeny_plat)
    dielce.push({
      nazov: "chrbat",
      x: vyska - 2 * t,
      y: sirka - 2 * t,
      z: t,
      ks: 1,
      cnc: true,
    });
  } else if (chrbat === "sololit") {
    // pribíjaný sololit 4 mm, (V−10) × (Š−10), len porez — overené
    // reálnym TopSolid exportom (korpus_vlozeny)
    dielce.push({
      nazov: "chrbat_sol",
      x: vyska - 10,
      y: sirka - 10,
      z: 4,
      ks: 1,
      cnc: false,
    });
  }

  // hĺbky vnútorných dielcov
  const hlbkaMedzist = hlbkaMedzisteny(hlbka, chrbat, t);
  const hlbkaPolice = hlbka - odpocetPolice(chrbat, t);

  // medzisteny + police + dvierka z otvorového stromu
  const celoCtx: CeloCtx = {
    t,
    innerW: sirka - 2 * t,
    innerH: vyska - 2 * t,
  };
  let mz = 0;
  let mv = 0;
  let policeSpolu = 0;
  let dvierkaIdx = 0;
  let zavesySpolu = 0;
  const zavesyPodlaTypu = new Map<TypCela, number>();
  let zasuvkaIdx = 0;
  let vysuvyPary = 0;
  let vysuvNlPouzity: number | null = null;
  function walk(o: Otvor): void {
    if (o.delenie) {
      const rect = rects.get(o.id);
      if (rect && osJeValidna(rect, o.delenie, t)) {
        if (o.delenie.smer === "vodorovne") {
          mv += 1;
          dielce.push({
            nazov: `medzistena_v${mv}`,
            x: rect.w,
            y: hlbkaMedzist,
            z: t,
            ks: 1,
            cnc: true,
          });
        } else {
          mz += 1;
          dielce.push({
            nazov: `medzistena_z${mz}`,
            x: rect.h,
            y: hlbkaMedzist,
            z: t,
            ks: 1,
            cnc: true,
          });
        }
        walk(o.delenie.deti[0]);
        walk(o.delenie.deti[1]);
      }
      return;
    }
    const rect = rects.get(o.id);
    if (!rect) return;
    if (o.police > 0) {
      policeSpolu += o.police;
      dielce.push({
        nazov: "polica",
        // vôľa 1 mm na šírku vnútra (reálne TopSolid exporty)
        x: rect.w - 1,
        y:
          hlbkaPolice -
          (policeZaVlozenymi(o) ? POLICA_SETBACK_VLOZENE : 0),
        z: t,
        ks: o.police,
        cnc: false,
      });
    }
    if (o.dvierka) {
      const { sirkaKridla, vyska: vyskaDvierok } = rozmerDvierok(
        rect,
        o.dvierka,
        celoCtx,
      );
      const typDvierok = normalizujTypCela(o.dvierka.typ);
      dvierkaIdx += 1;
      dielce.push({
        nazov:
          o.dvierka.kridla === 1
            ? `dvierka_${dvierkaIdx}`
            : `dvierka_${dvierkaIdx}_LP`,
        x: sirkaKridla,
        y: vyskaDvierok,
        z: t,
        ks: o.dvierka.kridla,
        smer: "Y",
        cnc: false,
      });
      const naKridlo =
        o.dvierka.zavesy ?? pocetZavesov(sirkaKridla, vyskaDvierok, t);
      const ks = o.dvierka.kridla * naKridlo;
      zavesySpolu += ks;
      zavesyPodlaTypu.set(
        typDvierok,
        (zavesyPodlaTypu.get(typDvierok) ?? 0) + ks,
      );
    }
    if (o.zasuvky && o.zasuvky.pocet > 0) {
      const nl = vyberNlVysuvu(hlbka);
      if (nl === null) {
        chyby.push(
          `Zásuvky v otvore: hĺbka korpusu ${hlbka} mm nestačí ani na ` +
            `najkratší výsuv (${VYSUV_NL_RAD[VYSUV_NL_RAD.length - 1]} + 4 mm).`,
        );
      } else {
        const n = o.zasuvky.pocet;
        const typZasuviek = normalizujTypCela(o.zasuvky.celoTyp);
        // čelá delia čelnú plochu (podľa typu), medzi čelami 2 mm
        const plocha = rozmerCelnejPlochy(rect, typZasuviek, celoCtx);
        const vyskaCela = Math.round(
          (plocha.h - (n - 1) * MEDZERA_CIEL) / n,
        );
        const sirkaCela = Math.round(plocha.w);
        // box podľa naučených TopSolid vzorcov: výška boku auto
        // z výšky otvoru (ručný override možný), šírka a dno podľa t
        // (pri 18 mm −63 a −47)
        const vyskaBoku =
          o.zasuvky.vyskaBoku ?? vyskaBokuZasuvky(rect.h, n, typZasuviek);
        const boxSirka = Math.round(rect.w - boxSirkaOdpocet(t));
        zasuvkaIdx += 1;
        const idx = zasuvkaIdx;
        dielce.push(
          {
            nazov: `zas${idx}_celo`,
            x: sirkaCela,
            y: vyskaCela,
            z: t,
            ks: n,
            smer: "Y",
            cnc: false,
          },
          { nazov: `zas${idx}_bok`, x: nl, y: vyskaBoku, z: t, ks: 2 * n, cnc: true },
          {
            nazov: `zas${idx}_box_celo`,
            x: boxSirka,
            y: vyskaBoku,
            z: t,
            ks: n,
            cnc: true,
          },
          {
            nazov: `zas${idx}_box_chrbat`,
            x: boxSirka,
            y: vyskaBoku - BOX_CHRBAT_NIZSI,
            z: t,
            ks: n,
            cnc: true,
          },
          {
            nazov: `zas${idx}_dno`,
            x: nl - DNO_NL_ODPOCET,
            y: Math.round(rect.w - dnoSirkaOdpocet(t)),
            z: 4,
            ks: n,
            cnc: false,
          },
        );
        vysuvyPary += n;
        vysuvNlPouzity = nl;
      }
    }
  }
  walk(root);

  // kovanie
  if (nohy.enabled) {
    const ks = nohy.pocetOverride ?? pocetNoh(sirka);
    kovanie.push({
      nazov: nohy.material?.nazov ?? `Nohy ${nohy.vyskaMm} mm`,
      materialId: nohy.material?.id ?? null,
      ks,
      poznamka: `výška ${nohy.vyskaMm} mm, ${nohy.pocetOverride != null ? "ručný počet" : "auto podľa šírky"}`,
    });
  }
  if (policeSpolu > 0) {
    kovanie.push({
      nazov: "podpera police",
      materialId: null,
      ks: policeSpolu * podperyNaPolicu(hlbka),
      poznamka: `${podperyNaPolicu(hlbka)}/policu (hĺbka ${hlbka})`,
    });
  }
  if (zavesySpolu > 0) {
    // závesy per typ dvierok — reálne dielenské kódy (56-3550/56-3690)
    for (const [typCela, ks] of zavesyPodlaTypu) {
      kovanie.push({
        nazov: params.zaves?.nazov ?? `Záves ${ZAVES_KATALOG[typCela]}`,
        materialId: params.zaves?.id ?? null,
        katalog: ZAVES_KATALOG[typCela],
        ks,
        poznamka: `${ZAVES_VARIANT[typCela]}; počet auto podľa hmotnosti krídla, ručne cez dvierka`,
      });
    }
    // krížová podložka CLIP 1:1 ku každému závesu (dielňa 07/2026)
    kovanie.push({
      nazov: `Krížová podložka CLIP ${PODLOZKA_KATALOG}`,
      materialId: null,
      katalog: PODLOZKA_KATALOG,
      ks: zavesySpolu,
      poznamka: "1 ks na každý záves",
    });
  }
  if (vysuvyPary > 0) {
    kovanie.push({
      nazov: params.vysuv?.nazov ?? "Výsuv (vyber z cenníka)",
      materialId: params.vysuv?.id ?? null,
      ks: vysuvyPary,
      poznamka: `NL ${vysuvNlPouzity} mm (hĺbka korpusu − 4 mm), 1 pár/zásuvku`,
    });
  }

  // validácie rozmerov
  const varovania: string[] = [];
  if (sirka < 100 || vyska < 100 || hlbka < 100) {
    chyby.push("Rozmery korpusu musia byť aspoň 100 mm.");
  }
  const sirkaPolice = Math.max(...[...rects.values()].map((r) => r.w), 0);
  if (policeSpolu > 0 && sirkaPolice > 964) {
    varovania.push(
      "Polica širšia než 964 mm — hrozí prehnutie, zváž T-profil alebo medzistenu.",
    );
  }

  return { dielce, kovanie, chyby, varovania };
}

// ─── Immutable úpravy stromu otvorov (pre UI) ────────────────

/** Vráti nový strom s otvorom `id` transformovaným cez `fn`. */
export function upravOtvor(
  root: Otvor,
  id: string,
  fn: (o: Otvor) => Otvor,
): Otvor {
  if (root.id === id) return fn(root);
  if (!root.delenie) return root;
  return {
    ...root,
    delenie: {
      ...root.delenie,
      deti: [
        upravOtvor(root.delenie.deti[0], id, fn),
        upravOtvor(root.delenie.deti[1], id, fn),
      ],
    },
  };
}

/** Rozdelí otvor medzistenou (police otvoru sa presunú do prvého dieťaťa). */
export function rozdelOtvor(
  root: Otvor,
  id: string,
  smer: Delenie["smer"],
  ref: Delenie["ref"],
  mm: number,
): Otvor {
  return upravOtvor(root, id, (o) => ({
    ...o,
    police: 0,
    dvierka: undefined,
    zasuvky: undefined,
    delenie: {
      smer,
      ref,
      mm,
      deti: [
        {
          ...novyOtvor(),
          police: o.police,
          dvierka: o.dvierka,
          zasuvky: o.zasuvky,
        },
        novyOtvor(),
      ],
    },
  }));
}

/** Upraví polohu existujúcej medzisteny (ref + mm), deti zostávajú. */
export function upravDelenie(
  root: Otvor,
  id: string,
  ref: Delenie["ref"],
  mm: number,
): Otvor {
  return upravOtvor(root, id, (o) =>
    o.delenie ? { ...o, delenie: { ...o.delenie, ref, mm } } : o,
  );
}

/** Zruší delenie otvoru (obsah detí sa zahodí). */
export function zrusDelenie(root: Otvor, id: string): Otvor {
  return upravOtvor(root, id, (o) => ({ id: o.id, police: 0 }));
}

/** Komponent z palety buildera — vkladá sa klikom do otvoru. */
export type KomponentKind =
  | "medzistena_v"
  | "medzistena_z"
  | "polica"
  | "dvierka1"
  | "dvierka2"
  | "zasuvka";

/**
 * Vloží komponent z palety do otvoru (immutable, vzor rozdelOtvor).
 *
 * - medzistena: rozdelí otvor na stred (poloha sa doladí klikom na ňu),
 * - polica: +1 polica,
 * - dvierka: nastaví 1/2 krídla (zruší zásuvky — vylučujú sa),
 * - zasuvka: +1 zásuvka (zruší dvierka).
 */
export function vlozKomponent(
  root: Otvor,
  id: string,
  kind: KomponentKind,
): Otvor {
  switch (kind) {
    case "medzistena_v":
      return rozdelOtvor(root, id, "vodorovne", "stred", 0);
    case "medzistena_z":
      return rozdelOtvor(root, id, "zvislo", "stred", 0);
    case "polica":
      return upravOtvor(root, id, (o) => ({
        ...o,
        police: o.police + 1,
      }));
    case "dvierka1":
    case "dvierka2":
      return upravOtvor(root, id, (o) => ({
        ...o,
        zasuvky: undefined,
        dvierka: {
          kridla: kind === "dvierka1" ? 1 : 2,
          typ: o.dvierka ? normalizujTypCela(o.dvierka.typ) : "nalozene",
        },
      }));
    case "zasuvka":
      return upravOtvor(root, id, (o) => ({
        ...o,
        dvierka: undefined,
        zasuvky: {
          pocet: (o.zasuvky?.pocet ?? 0) + 1,
          vyskaBoku: o.zasuvky?.vyskaBoku ?? null,
          celoTyp: o.zasuvky ? normalizujTypCela(o.zasuvky.celoTyp) : "nalozene",
        },
      }));
  }
}

/** Normalizuje typy čiel v celom strome (legacy "nasadene" z uložených
 *  konfigov/šablón) — volať pri načítaní konfigu. */
export function normalizujOtvorTypy(root: Otvor): Otvor {
  const dvierka = root.dvierka
    ? { ...root.dvierka, typ: normalizujTypCela(root.dvierka.typ) }
    : root.dvierka;
  const zasuvky = root.zasuvky
    ? { ...root.zasuvky, celoTyp: normalizujTypCela(root.zasuvky.celoTyp) }
    : root.zasuvky;
  return {
    ...root,
    dvierka,
    zasuvky,
    delenie: root.delenie
      ? {
          ...root.delenie,
          deti: [
            normalizujOtvorTypy(root.delenie.deti[0]),
            normalizujOtvorTypy(root.delenie.deti[1]),
          ],
        }
      : root.delenie,
  };
}

/** Nájde otvor podľa id. */
export function najdiOtvor(root: Otvor, id: string): Otvor | null {
  if (root.id === id) return root;
  if (!root.delenie) return null;
  return (
    najdiOtvor(root.delenie.deti[0], id) ??
    najdiOtvor(root.delenie.deti[1], id)
  );
}
